import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Clock, Sparkles, Users, Leaf, BadgeCheck,
  ArrowRight, Home as HomeIcon, Building2, SprayCan, Truck, Quote, Star,
} from "lucide-react";

import { Hero } from "../components/site/Hero";
import { GoogleReviewsBadge } from "../components/site/GoogleReviewsBadge";
import { usePageMeta } from "../hooks/usePageMeta";

const TEASER_SERVICES = [
  { icon: HomeIcon, title: "Huishoudelijke schoonmaak", desc: "Vlekkeloze woning, wekelijks of eenmalig.", to: "/diensten#huishoudelijk" },
  { icon: Building2, title: "Kantoorschoonmaak", desc: "Representatieve werkplek, dagelijks of periodiek.", to: "/diensten#kantoor" },
  { icon: SprayCan, title: "Dieptereiniging", desc: "Grondig én hygiënisch — tot in elk hoekje.", to: "/diensten#diepte" },
  { icon: Truck, title: "Schoonmaak na verhuis", desc: "Klare oplevering of frisse start in uw nieuwe woning.", to: "/diensten#verhuis" },
];

const REASONS = [
  { icon: ShieldCheck, title: "Volledig verzekerd", desc: "Werken in vol vertrouwen — wij zijn 100% verzekerd voor schade en aansprakelijkheid." },
  { icon: Clock, title: "24/7 bereikbaar", desc: "Of het nu vroeg in de ochtend of laat 's avonds is — wij zijn er voor u." },
  { icon: Users, title: "Vaste, ervaren ploeg", desc: "Vast team met oog voor detail. U kent uw poetshulp persoonlijk." },
  { icon: Leaf, title: "Milieubewuste producten", desc: "Effectief én eco-vriendelijk — veilig voor kinderen, huisdieren en planeet." },
  { icon: BadgeCheck, title: "10+ jaar ervaring", desc: "Bedrijven en particulieren in heel Antwerpen vertrouwen op onze expertise." },
  { icon: Sparkles, title: "Garantie op resultaat", desc: "Niet 100% tevreden? Wij komen kosteloos terug tot het perfect is." },
];

const HOME_REVIEWS = [
  { text: "Geweldige service en altijd perfect schoon. Het team is vriendelijk, stipt en denkt mee.", author: "Marie Peeters", role: "Office Manager, Antwerpen-Zuid" },
  { text: "Betrouwbaar, snel en 24/7 bereikbaar. Na de opleverings­schoonmaak was alles spotless.", author: "Jan S.", role: "Zaakvoerder, Berchem" },
  { text: "Onze ramen blinken al maanden. Vakwerk en altijd correcte communicatie.", author: "Lieselot V.", role: "Particulier, Eilandje" },
];

export default function HomePage() {
  usePageMeta({
    title: "Dila Group BV — Professioneel schoonmaakbedrijf in Antwerpen | 24/7",
    description:
      "Dila Group BV biedt 24/7 professionele schoonmaakdiensten in Antwerpen: huishoudelijk, kantoor, dieptereiniging en verhuisschoonmaak. Vraag uw gratis offerte aan.",
    path: "/",
  });

  return (
    <>
      <Hero />
      <GoogleReviewsBadge />

      {/* Teaser services */}
      <section className="py-24 sm:py-32 bg-[#F4F7F6]" data-testid="home-services-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">Onze Diensten</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] text-balance max-w-2xl">
                Eén partner. Vier schoonmaak­oplossingen op maat.
              </h2>
            </div>
            <Link to="/diensten" data-testid="home-services-cta" className="inline-flex items-center gap-1.5 font-semibold text-[#0284C7] hover:text-[#0369A1]">
              Bekijk alle diensten <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TEASER_SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={s.to}
                  data-testid={`home-service-link-${i}`}
                  className="group block rounded-3xl bg-white border border-slate-200 p-6 h-full hover:border-[#0284C7]/40 hover:-translate-y-1 hover:shadow-xl transition-all"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F0FDF4] text-[#16A34A] group-hover:bg-[#0284C7] group-hover:text-white transition-colors">
                    <s.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-[#0F172A]">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0284C7]">
                    Meer info <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-24 sm:py-32 bg-white" id="waarom" data-testid="home-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">Waarom kiezen voor ons</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] text-balance">
              Niet zomaar schoon. <span className="text-[#0284C7]">Dila-schoon.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Wat ons onderscheidt is de combinatie van vakmanschap, betrouwbaarheid en zorg voor elk detail.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`reason-card-${i}`}
                className="rounded-3xl bg-[#F4F7F6] border border-slate-200 p-7 hover:border-[#0284C7]/40 hover:bg-white hover:shadow-md transition-all"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-slate-200 text-[#0284C7]">
                  <r.icon size={22} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-[#0F172A]">{r.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews teaser */}
      <section className="py-24 sm:py-32 bg-gradient-to-b from-white via-[#F4F7F6] to-white" data-testid="home-reviews-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">Klantbeoordelingen</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F172A] text-balance">
                Vertrouwd door honderden klanten in Antwerpen.
              </h2>
            </div>
            <Link to="/reviews" className="inline-flex items-center gap-1.5 font-semibold text-[#0284C7] hover:text-[#0369A1]" data-testid="home-reviews-cta">
              Alle reviews <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {HOME_REVIEWS.map((r, i) => (
              <figure key={r.author} className="relative rounded-3xl bg-white border border-slate-200 p-7 hover:border-[#0284C7]/40 hover:shadow-lg transition-all" data-testid={`home-review-${i}`}>
                <Quote className="absolute top-5 right-5 text-[#0284C7]/15" size={44} />
                <div className="flex items-center gap-1 text-[#16A34A]">
                  {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={14} fill="currentColor" strokeWidth={0} />)}
                </div>
                <blockquote className="mt-4 font-quote text-lg text-[#0F172A] leading-snug italic">“{r.text}”</blockquote>
                <figcaption className="mt-5">
                  <p className="font-display font-bold text-[#0F172A] text-sm">{r.author}</p>
                  <p className="text-xs text-slate-500">{r.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-20 bg-[#0F172A] text-white relative overflow-hidden" data-testid="home-cta-banner">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-16 h-[320px] w-[320px] rounded-full bg-[#0284C7]/25 blur-3xl" />
          <div className="absolute -bottom-24 -right-16 h-[320px] w-[320px] rounded-full bg-[#16A34A]/20 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-balance">
              Klaar voor een vlekkeloze samenwerking?
            </h2>
            <p className="mt-3 text-slate-300 text-lg">
              Ontvang binnen 24u een vrijblijvende offerte op maat van uw situatie.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="home-cta-quote-banner"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-7 py-4 shadow-xl shadow-sky-700/30 transition-all hover:-translate-y-0.5"
          >
            Offerte aanvragen <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
